import { Directive, effect, ElementRef, inject, input, OnDestroy, Renderer2 } from '@angular/core';

type BadgePosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

@Directive({
  selector: '[appBadge]',
})
export class BadgeDirective implements OnDestroy {
  #host = inject(ElementRef);
  #renderer = inject(Renderer2);

  readonly _value = input.required<string, number | string>({
    alias: 'appBadge',
    transform: (value) => value.toString().trim(),
  });
  readonly _position = input<BadgePosition>('top-right');

  #badgeElement?: HTMLElement;
  #cleanupEffect?: () => void;

  constructor() {
    // réagit automatiquement aux changements des signaux
    const ef = effect(() => {
      const value = this._value();
      const position = this._position();

      if (!value) {
        this.#removeBadge();
        return;
      }
      this.updateBadge(value, position);
    });
    this.#cleanupEffect = ef.destroy?.bind(ef);
  }

  ngOnDestroy(): void {
    this.#cleanupEffect?.();
    this.#removeBadge();
  }

  private updateBadge(value: string, position: BadgePosition) {
    if (!this.#badgeElement) {
      this.createBadge();
    }
    this.#badgeElement!.textContent = value;
    this.#applyTailwindClasses(position);
  }

  private createBadge() {
    const hostEl = this.#host.nativeElement;

    // position relative sur le host (utile avec Tailwind)
    this.#renderer.addClass(hostEl, 'relative');

    const badge = this.#renderer.createElement('span');
    this.#badgeElement = badge;
    this.#renderer.appendChild(hostEl, badge);
  }

  #removeBadge() {
    if (this.#badgeElement) {
      this.#renderer.removeChild(this.#host.nativeElement, this.#badgeElement);
      this.#badgeElement = undefined;
    }
  }

  #applyTailwindClasses(position: BadgePosition) {
    const badge = this.#badgeElement!;
    // reset minimal
    badge.className = '';

    // base badge (tailwind)
    this.#renderer.addClass(badge, 'inline-flex');
    this.#renderer.addClass(badge, 'items-center');
    this.#renderer.addClass(badge, 'justify-center');
    this.#renderer.addClass(badge, 'component-small-medium');
    this.#renderer.addClass(badge, 'text-components-content-primary-darker');
    this.#renderer.addClass(badge, 'bg-components-background-primary-lightest');
    this.#renderer.addClass(badge, 'rounded-full');
    this.#renderer.addClass(badge, 'px-1.5');
    this.#renderer.addClass(badge, 'size-lg');
    this.#renderer.addClass(badge, 'absolute');
    this.#renderer.addClass(badge, 'translate-x-1/2');
    this.#renderer.addClass(badge, '-translate-y-1/2');

    // position
    switch (position) {
      case 'top-right':
        this.#renderer.addClass(badge, 'top-0');
        this.#renderer.addClass(badge, 'right-0');
        break;
      case 'top-left':
        this.#renderer.addClass(badge, 'top-0');
        this.#renderer.addClass(badge, 'left-0');
        this.#renderer.removeClass(badge, 'translate-x-1/2');
        this.#renderer.addClass(badge, '-translate-x-1/2');
        break;
      case 'bottom-right':
        this.#renderer.addClass(badge, 'bottom-0');
        this.#renderer.addClass(badge, 'right-0');
        this.#renderer.removeClass(badge, '-translate-y-1/2');
        this.#renderer.addClass(badge, 'translate-y-1/2');
        break;
      case 'bottom-left':
        this.#renderer.addClass(badge, 'bottom-0');
        this.#renderer.addClass(badge, 'left-0');
        this.#renderer.removeClass(badge, 'translate-x-1/2');
        this.#renderer.addClass(badge, '-translate-x-1/2');
        this.#renderer.removeClass(badge, '-translate-y-1/2');
        this.#renderer.addClass(badge, 'translate-y-1/2');
        break;
    }
  }
}
